import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterParow() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-parow",
    name: "Painters Parow",
    location: "Parow, Cape Town",
    rating: 4.7,
    reviews: 128,
    specialties: ["Residential Painting", "Commercial Painting", "Interior & Exterior", "Roof Coating"],
    image: "https://paintersparow.co.za/wp-content/uploads/2024/12/Painters-Parow-1.webp",
    verified: true,
    yearsExperience: 15,
    website: "https://paintersparow.co.za",
    email: "info@paintersparow.co.za",
    phone: "+27 21 930 5555",
    description: "Painters Parow is a trusted painting contractor serving Parow and the Northern Suburbs of Cape Town. With over 15 years of experience, we provide comprehensive painting solutions for residential and commercial properties. Our skilled team is committed to delivering exceptional results using quality materials and proven techniques.",
    services: [
      "Residential Interior Painting",
      "Residential Exterior Painting", 
      "Commercial Painting Services",
      "Roof Painting & Coating",
      "Waterproofing Solutions",
      "Damp Proofing",
      "Wall Repairs & Preparation",
      "Texture Coating",
      "Color Consultation",
      "Maintenance Services"
    ],
    serviceAreas: ["Parow", "Parow North", "Parow East", "Bellville", "Goodwood", "Elsies River", "Brooklyn", "Northern Suburbs"],
    gallery: [
      "https://paintersparow.co.za/wp-content/uploads/2024/12/Painters-Parow-1.webp",
      "https://paintersparow.co.za/wp-content/uploads/2024/12/Painters-Parow-2.webp",
      "https://paintersparow.co.za/wp-content/uploads/2024/12/Painters-Parow-3.webp",
      "https://paintersparow.co.za/wp-content/uploads/2024/12/Painters-Parow-4.webp",
      "https://paintersparow.co.za/wp-content/uploads/2024/12/Painters-Parow-5.webp",
      "https://paintersparow.co.za/wp-content/uploads/2024/12/Painters-Parow-6.webp"
    ],
    testimonials: [
      {
        name: "David Johnson",
        rating: 5,
        comment: "Painters Parow did an excellent job on our home. Professional team, quality work, and completed on time. Highly recommend their services for any painting needs in the Northern Suburbs."
      },
      {
        name: "Sarah Williams", 
        rating: 5,
        comment: "We hired Painters Parow for our commercial property and they exceeded expectations. The team was professional, efficient, and the quality is outstanding. Will definitely use them again."
      },
      {
        name: "Mark Thompson",
        rating: 4,
        comment: "Great service and workmanship. They painted our house exterior and did roof coating. Very satisfied with the results and would recommend them to others."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Parow | Professional House Painting Services Cape Town | Residential & Commercial";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Parow offers professional painting services in Parow, Cape Town. Specializing in residential, commercial, interior and exterior painting with over 15 years of experience. Trusted local painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Parow, painters Parow, painting contractors Cape Town, residential painting, commercial painting, roof coating');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${painter.image})` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About {painter.name}</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://paintersparow.co.za/wp-content/uploads/2024/12/Painters-Parow-2.webp"
                      alt="Painters Parow professional painting services Cape Town Northern Suburbs"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-phone-line mr-3 text-lime-500"></i>
                      <a href={`tel:${painter.phone}`} className="text-gray-700 hover:text-lime-600">
                        {painter.phone}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} with quality materials and expert craftsmanship.</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Project Gallery</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews</h2>
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-phone-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Phone</h3>
                      <a href={`tel:${painter.phone}`} className="text-gray-700 hover:text-lime-600">
                        {painter.phone}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and Northern Suburbs</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get a Quote</h3>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
